import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import './Leaderboard.css'
import '../replay/ReplayList.css'
import { formatDate } from './constants';
import { useNavigate } from 'react-router-dom';
import moment from "moment-timezone";
import PullToRefresh from "react-simple-pull-to-refresh";
import { RefreshLoadingModal } from "../RefreshLoadingModal";
import { useLegacyAuth } from '../../hooks/useLegacyAuth';

// API base URL - use relative path to work with local server
const API_BASE_URL = '/api/scores';

// Map level_ids to display names
const LEVEL_ID_MAP = {
    'nakamigo_crush': 'Pixel Crush',
    'house_songs': 'House Songs',
    'tinydinos': "Let's F'ing Rawr",
    'mfers_basic': 'Mfers Jus Getting Started',
    'dark_pepe': 'Dark Pepe Rising',
    'lil_mfers': 'Lil Mfers Playground'
};


const CountdownTimer = () => {
    const [timeLeft, setTimeLeft] = useState("");
  
    useEffect(() => {
      const targetDate = moment.tz('2023-10-01 23:39:00', 'America/New_York');
      
      const intervalId = setInterval(() => {
        const now = moment();
        const duration = moment.duration(targetDate.diff(now));
        
        if (duration.asMilliseconds() <= 0) {
          clearInterval(intervalId); // Clear interval if the target date is reached
          setTimeLeft("Time's up!");
        } else {
          const days = Math.floor(duration.asDays());
          const hours = duration.hours();
          const minutes = duration.minutes();
          const seconds = duration.seconds();
          setTimeLeft(`${days} day ${hours} hours ${minutes} minutes ${seconds} seconds`);
        }
      }, 1000); // Update every second
      
      return () => clearInterval(intervalId); // Clear interval on component unmount
    }, []);
  
    return <div>Time Left in Competition: {timeLeft}</div>;
  };
  

function Leaderboard() {
    const [scores, setScores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('recent');
    const [showModal, setShowModal] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrev, setHasPrev] = useState(false);
    const [myGamesSort, setMyGamesSort] = useState('date'); // 'date' or 'score'
    const navigate = useNavigate();

    // Get auth state via legacy hook (supports both new auth and legacy localStorage)
    const { twitterUser, userId } = useLegacyAuth();

    const FILTER_CONFIGS = {
        recent: {
            displayName: "Recent Plays",
            sortRecent: true,
            collapseToUser: false,
            onlyTwitter: false,
            queryParams: () => ({
                sort_by: 'date',
                sort_order: 'desc',
                limit: 100
            }),
            style: {
                backgroundColor: "#FEAC64" // Orange
            },
            visibility: true
        },
        my_games: {
            displayName: "My Games",
            sortRecent: false, // Controlled by myGamesSort state
            collapseToUser: false,
            onlyTwitter: false,
            queryParams: () => ({
                sort_by: myGamesSort,
                sort_order: 'desc',
                limit: 20,
                page: page
            }),
            style: {
                backgroundColor: "#4CAF50" // Green
            },
            visibility: !!(userId || twitterUser) // Visible when user is signed in (new auth or legacy)
        },
        sprint_levels: {
            displayName: "Sprint Levels (2 min)",
            sortRecent: false,
            collapseToUser: false, // Custom logic: group by user x level
            onlyTwitter: false,
            queryParams: () => ({
                sort_by: 'score',
                sort_order: 'desc',
                limit: 500,
                level_id: 'nakamigo_crush,house_songs,tinydinos,dark_pepe,mfers_basic'
            }),
            style: {
                backgroundColor: "#FF0000" // Red
            },
            visibility: true
        }
    };

    async function fetchData() {
        const currentConfig = FILTER_CONFIGS[filter];
        const params = currentConfig.queryParams();

        let url;

        // Special handling for my_games filter
        // Use userId from new auth system if available, otherwise fall back to legacy twitterUser.user_id
        const userIdForQuery = userId || twitterUser?.user_id;
        if (filter === 'my_games' && userIdForQuery) {
            const queryString = new URLSearchParams(params).toString();
            url = `${API_BASE_URL}/user/${userIdForQuery}/games${queryString ? '?' + queryString : ''}`;
        } else {
            const queryString = new URLSearchParams(params).toString();
            url = `${API_BASE_URL}${queryString ? '?' + queryString : ''}`;
        }

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const responseData = await response.json();

            // Extract scores/games array from response
            let data;
            if (filter === 'my_games') {
                data = responseData.games || [];
                // Update pagination state for my_games
                if (responseData.pagination) {
                    setTotalPages(responseData.pagination.totalPages);
                    setHasNext(responseData.pagination.hasNext);
                    setHasPrev(responseData.pagination.hasPrev);
                }
            } else {
                data = responseData.scores || responseData;
                // Reset pagination for non-my_games filters
                setPage(1);
                setTotalPages(1);
                setHasNext(false);
                setHasPrev(false);
            }

            // Debug logging: show full score data including display_info
            console.log('[Mfersbeheaded Leaderboard] Received scores:', {
                filter,
                count: data.length,
                sampleScores: data.slice(0, 3).map(s => ({
                    id: s.id || s.score_id,
                    score: s.score,
                    player_name: s.player_name,
                    user_id: s.user_id,
                    twitter_user_id: s.twitter_user_id,
                    twitter_user: s.twitter_user,
                    display_info: s.display_info,
                    level_name: s.level_name
                }))
            });

            // Filter out test accounts
            const unwantedNames = ["nngmi_test", "nngmi_test_2", "nngmi_tes", "nngmi_test_all_"];
            let validScores = data.filter(score => {
                if (score.player_name) {
                    const nameLower = score.player_name.toLowerCase();
                    return !unwantedNames.some(unwanted => nameLower === unwanted.toLowerCase());
                }
                return true;
            });

            // Group by user and keep highest score
            function getHighestScoresByUser(scores) {
                const grouped = {};

                scores.forEach(score => {
                    // Use twitter screen_name if available, otherwise player_name
                    const key = (score.twitter_user?.screen_name || score.player_name || 'anon').toLowerCase().trim();
                    if (!grouped[key] || grouped[key].score < score.score) {
                        grouped[key] = score;
                    }
                });

                return Object.values(grouped);
            }

            // Group by user x level and keep highest score per combination
            function getHighestScoresByUserAndLevel(scores) {
                const grouped = {};

                scores.forEach(score => {
                    // Use twitter screen_name if available, otherwise player_name
                    const userName = (score.twitter_user?.screen_name || score.player_name || 'anon').toLowerCase().trim();
                    const key = `${userName}|${score.level_id}`;
                    if (!grouped[key] || grouped[key].score < score.score) {
                        grouped[key] = score;
                    }
                });

                return Object.values(grouped);
            }

            let displayedScores = [...validScores];

            // Skip grouping and sorting for my_games (already handled by backend)
            if (filter !== 'my_games') {
                // For sprint levels, group by user x level (allow multiple entries per user)
                if (filter === 'sprint_levels') {
                    displayedScores = getHighestScoresByUserAndLevel(displayedScores);
                }
                // If there's a need to collapse scores to a single user (for other filters)
                else if (currentConfig.collapseToUser) {
                    displayedScores = getHighestScoresByUser(displayedScores);
                }

                // Filter to only Twitter users if needed
                if (currentConfig.onlyTwitter) {
                    displayedScores = displayedScores.filter(score => score.twitter_user?.screen_name);
                }

                // Sort is already handled by API, but apply client-side if needed
                if (currentConfig.sortRecent) {
                    displayedScores.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                } else {
                    displayedScores.sort((a, b) => b.score - a.score);
                }
            }

            setScores(displayedScores);
            setLoading(false);
        } catch (error) {
            logger.error("Error fetching scores:", error);
            setError(error);
            setLoading(false);
        }
    }

    const handleRefresh = async () => {
        return new Promise((resolve) => {
            // Show modal immediately on refresh
            setShowModal(true);

            // Re-fetch data
            fetchData().then(() => {
                // Wait at least 800ms to ensure animation shows
                setTimeout(() => {
                    setShowModal(false);
                    resolve();
                }, 800);
            });
        });
    };

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        setPage(1); // Reset to first page when changing filters
    };

    const handleNextPage = () => {
        if (hasNext) {
            setPage(prevPage => prevPage + 1);
        }
    };

    const handlePrevPage = () => {
        if (hasPrev) {
            setPage(prevPage => Math.max(1, prevPage - 1));
        }
    };

    const toggleMyGamesSort = () => {
        setMyGamesSort(prevSort => prevSort === 'date' ? 'score' : 'date');
        setPage(1); // Reset to first page when changing sort
    };


    useEffect(() => {
        fetchData();
    }, [filter, page, myGamesSort]);
    


    if (loading) return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: '1rem'
        }}>
            <div className="spinner"></div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Loading Leaderboard...</div>
        </div>
    );
    if (error) return <div>Sorry - could not load Leaderboard!</div>;

    return (
        <>
        <PullToRefresh
            onRefresh={handleRefresh}
            pullingContent=""
            refreshingContent=""
            className="min-h-screen"
        >
        <div>
            <div className="leaderboard-container">
                <table>
                    <thead>
                        <tr>
                            <th colSpan="6">
                                <h1 className="leaderboard-title">
                                    {filter === "recent" ? "Recent Plays" :
                                     filter === "my_games" ? "My Games" :
                                     `Leaderboard - ${FILTER_CONFIGS[filter].displayName}`}
                                </h1>
                                <p>
                                    {filter === "sprint_levels" ? "Highest score per user per level" :
                                     filter === "my_games" ? `Showing page ${page} of ${totalPages}` :
                                     (FILTER_CONFIGS[filter].collapseToUser === true ? "Only highest score per user shown" : "")}
                                </p>
                                <div className="level-buttons">
                                    <button onClick={() => navigate('/mfersbeheaded')} className="nav-button" style={{ backgroundColor: 'darkred' }}>Back</button>
                                    {Object.keys(FILTER_CONFIGS).map(filterKey => {
                                        const currentConfig = FILTER_CONFIGS[filterKey];
                                        const currentStyle = currentConfig.style;

                                        // Check for visibility
                                        if (currentConfig.visibility) {
                                            const buttonClass = filterKey === 'recent' ? 'recent-plays-button' : 'level-button agg-button';
                                            return (
                                                <button
                                                    key={filterKey}
                                                    style={currentStyle}
                                                    onClick={() => handleFilterChange(filterKey)}
                                                    className={buttonClass}
                                                >
                                                    {currentConfig.displayName}
                                                </button>
                                            );
                                        }
                                        return null; // Return null if the button should not be rendered
                                    })}
                                    {filter === 'my_games' && (
                                        <button
                                            onClick={toggleMyGamesSort}
                                            className="level-button agg-button"
                                            style={{ backgroundColor: '#2196F3' }}
                                        >
                                            Sort: {myGamesSort === 'date' ? 'Recent' : 'High Score'}
                                        </button>
                                    )}
                                </div>
                                {filter === 'my_games' && (
                                    <div className="pagination-controls" style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: '10px',
                                        marginTop: '10px',
                                        alignItems: 'center'
                                    }}>
                                        <button
                                            onClick={handlePrevPage}
                                            disabled={!hasPrev}
                                            className="nav-button"
                                            style={{
                                                backgroundColor: hasPrev ? '#4CAF50' : '#ccc',
                                                cursor: hasPrev ? 'pointer' : 'not-allowed'
                                            }}
                                        >
                                            ← Previous
                                        </button>
                                        <span style={{ fontWeight: 'bold' }}>
                                            Page {page} of {totalPages}
                                        </span>
                                        <button
                                            onClick={handleNextPage}
                                            disabled={!hasNext}
                                            className="nav-button"
                                            style={{
                                                backgroundColor: hasNext ? '#4CAF50' : '#ccc',
                                                cursor: hasNext ? 'pointer' : 'not-allowed'
                                            }}
                                        >
                                            Next →
                                        </button>
                                    </div>
                                )}


                            </th>
                        </tr>
                        <tr>
                            {filter === 'sprint_levels' && <th>Rank</th>}
                            <th>Name</th>
                            <th>Score</th>
                            <th className="hide-mobile">Level</th>
                            <th className="hide-mobile">Date</th>
                            <th>Replay</th>
                        </tr>
                    </thead>
                    <tbody>
        {scores.map((score, index) => {
            const levelNameStyle = {
                fontSize: score.level_name === 'Pixel Crush' ? '0.75em' : '0.9em',
                fontFamily: score.level_name === 'Pixel Crush' ? 'nakamigo, sans-serif' : 'Sartoshi, sans-serif'
            };
            // Use display_info.twitter_handle (from server enrichment) or fall back to legacy twitter_user
            const twitterScreenName = score.display_info?.twitter_handle || score.twitter_user?.screen_name;
            const isRegisteredUser = !!(score.user_id || score.twitter_user_id);
            const hasReplay = score.replay_session_id && score.replay_session?.is_public;
            return (
                <tr
                    style={levelNameStyle}
                    key={score.id || score.score_id}
                    className={hasReplay ? 'clickable-row' : ''}
                    onClick={() => hasReplay && navigate(`/mfersbeheaded/replay/${score.replay_session_id}`)}
                >
                    {filter === 'sprint_levels' && <td>{index + 1}</td>}
                    <td>
                        {twitterScreenName ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                <a
                                    href={`https://x.com/${twitterScreenName}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="x-logo-fallback"
                                >
                                    <img
                                        src="/images/x-logo-black.png"
                                        alt="X"
                                        className="x-logo-fallback-img"
                                    />
                                </a>
                                <span className="verified-check">✓</span>
                                <a
                                    href={`https://x.com/${twitterScreenName}`}
                                    style={{ color: '#000', textDecoration: 'none' }}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    @{twitterScreenName}
                                </a>
                            </div>
                        ) : isRegisteredUser ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                <span className="verified-check">✓</span>
                                <span>{score.player_name || 'anon'}</span>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center' }}>
                                {score.player_name || 'anon'}
                            </div>
                        )}
                    </td>
                    <td data-tooltip="true">{score.score}</td>
                    <td className="hide-mobile" style={{ levelNameStyle }}>
                        {score.level_name}
                    </td>
                    <td className="hide-mobile">{formatDate(score.created_at)}</td>
                    <td>
                        {score.replay_session_id && score.replay_session?.is_public ? (
                            score.game_state === 1 ? (
                                <div className="live-indicator-container">
                                    <span className="live-indicator">LIVE</span>
                                    <button
                                        onClick={() => navigate(`/mfersbeheaded/replay/${score.replay_session_id}`)}
                                        className="watch-button live-watch"
                                    >
                                        Watch
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => navigate(`/mfersbeheaded/replay/${score.replay_session_id}`)}
                                    className="watch-button"
                                >
                                    Watch
                                </button>
                            )
                        ) : (
                            <span style={{ color: '#999', fontSize: '0.9em' }}>N/A</span>
                        )}
                    </td>
                </tr>
            );
        })}
    </tbody>
                </table>
            </div>
        </div>
        </PullToRefresh>

        {/* Refresh loading modal - shows during data refresh */}
        <RefreshLoadingModal show={showModal} />
        </>
    );
}

export default Leaderboard;
